import type { SupabaseClient } from '@supabase/supabase-js';
import type { IntegrationRow } from '@teslyn/domain';

/** Every column a signed-in member may read (never the Vault reference, which is not granted). */
const COLUMNS =
  'id, tenant_id, provider, label, kind, status, last_four, created_by, created_at, updated_at, last_used_at, last_error, monthly_calls, calls_month';

export async function loadIntegrations(sb: SupabaseClient, tenantId: string): Promise<IntegrationRow[]> {
  const { data, error } = await sb
    .from('tenant_integration')
    .select(COLUMNS)
    .eq('tenant_id', tenantId)
    .order('provider')
    .order('label');
  if (error) throw new Error(`the integrations could not be read (${error.message})`);
  return (data ?? []) as IntegrationRow[];
}

/** The value typed in the form, as the Edge Function expects it. It is sent once and never kept in the page. */
export type CredentialInput =
  | { readonly api_key: string }
  | { readonly username: string; readonly password: string }
  | { readonly url: string };

/** How long the page waits for Teslyn Cloud's answer to a save or remove before it says so. */
export const INTEGRATION_CALL_TIMEOUT_MS = 30_000;

/**
 * The words of a failed call to `integration-key-set`, so a save or remove that fails always says why (MB, 3 Nov
 * 2026: the Claude key's Save "did nothing"): the function's own words when it gave some, else the HTTP status, the
 * connection, or the time allowed. The typed value is never part of a message.
 */
export async function functionFailureWords(error: {
  readonly name?: string;
  readonly message: string;
  readonly context?: unknown;
}): Promise<string> {
  const ctx = error.context;
  if (ctx instanceof Response) {
    try {
      const j = (await ctx.clone().json()) as { error?: unknown };
      if (typeof j.error === 'string' && j.error.trim()) return j.error;
    } catch {
      /* not the function's JSON: a gateway or relay page */
    }
    return `Teslyn Cloud refused the request (HTTP ${ctx.status}) without saying why. Try again; if it keeps happening, tell Braynewave.`;
  }
  if (error.name === 'FunctionsFetchError') {
    const cause = ctx as { name?: unknown } | null;
    if (cause && typeof cause === 'object' && cause.name === 'AbortError')
      return `Teslyn Cloud did not answer within ${INTEGRATION_CALL_TIMEOUT_MS / 1000} seconds. Try again.`;
    return 'Teslyn Cloud could not be reached. Check the connection and try again.';
  }
  return error.message || 'Teslyn Cloud did not answer. Try again.';
}

async function call(sb: SupabaseClient, body: Record<string, unknown>): Promise<unknown> {
  const { data, error } = await sb.functions.invoke<{ error?: string }>('integration-key-set', {
    body,
    timeout: INTEGRATION_CALL_TIMEOUT_MS,
  });
  if (error) throw new Error(await functionFailureWords(error as { name?: string; message: string }));
  return data;
}

/** Add an integration (no `integrationId`) or replace the stored value of one. */
export async function saveIntegration(
  sb: SupabaseClient,
  req: {
    readonly tenantId: string;
    readonly provider: string;
    readonly label: string;
    readonly integrationId: string | null;
    readonly value: CredentialInput;
  },
): Promise<void> {
  await call(sb, {
    tenant_id: req.tenantId,
    action: 'set',
    provider: req.provider,
    label: req.label,
    integration_id: req.integrationId,
    value: req.value,
  });
}

export async function removeIntegration(
  sb: SupabaseClient,
  tenantId: string,
  integrationId: string,
): Promise<void> {
  await call(sb, { tenant_id: tenantId, action: 'remove', integration_id: integrationId });
}

// ---------------------------------------------------------------------------------------------- file archive

/** The tenant's file archive (public.tenant_archive; docs/specs/cloud/file-archive.md §6). */
export interface ArchiveRow {
  readonly destination: 'survera' | 'nextcloud';
  /** Decision 393: archiving on or off for the tenant. */
  readonly enabled: boolean;
  readonly nextcloud_url: string | null;
  readonly nextcloud_folder: string | null;
  readonly nextcloud_username: string | null;
  readonly last_success_at: string | null;
  readonly last_success_path: string | null;
  readonly last_error: string | null;
  readonly last_error_at: string | null;
  readonly last_test_at: string | null;
  readonly last_test_ok: boolean | null;
  readonly last_test_message: string | null;
}

const ARCHIVE_COLUMNS =
  'destination, enabled, nextcloud_url, nextcloud_folder, nextcloud_username, last_success_at, last_success_path, last_error, last_error_at, last_test_at, last_test_ok, last_test_message';

/** The archive row, or null when the tenant has never set anything up or archived anything (Teslyn Cloud). */
export async function loadArchive(sb: SupabaseClient, tenantId: string): Promise<ArchiveRow | null> {
  const { data, error } = await sb
    .from('tenant_archive')
    .select(ARCHIVE_COLUMNS)
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) throw new Error(`the file archive could not be read (${error.message})`);
  return (data ?? null) as ArchiveRow | null;
}

/** Set up or replace the Nextcloud: the app password goes to Vault once and is never kept in the page. */
export async function saveNextcloud(
  sb: SupabaseClient,
  req: {
    readonly tenantId: string;
    readonly integrationId: string | null;
    readonly url: string;
    readonly folder: string;
    readonly username: string;
    readonly appPassword: string;
  },
): Promise<void> {
  await call(sb, {
    tenant_id: req.tenantId,
    action: 'set',
    provider: 'nextcloud',
    label: '',
    integration_id: req.integrationId,
    value: { username: req.username, password: req.appPassword },
    settings: { url: req.url, folder: req.folder },
  });
}

export interface ConnectionTest {
  readonly ok: boolean;
  readonly message: string;
}

/** "Test connection": Teslyn Cloud signs in to the Nextcloud with the stored app password and checks the folder. */
export async function testNextcloud(sb: SupabaseClient, tenantId: string): Promise<ConnectionTest> {
  const { data, error } = await sb.functions.invoke<{ ok?: boolean; message?: string; error?: string }>(
    'archive-upload',
    { body: { action: 'test', tenant_id: tenantId } },
  );
  if (error) return { ok: false, message: await functionFailureWords(error as { message: string }) };
  return {
    ok: data?.ok === true,
    message: data?.message ?? data?.error ?? 'Teslyn Cloud did not answer the test.',
  };
}

/** Decision 393: a tenant admin switches archiving off or on (public.archive_enabled_set, as the signed-in user). */
export async function setArchiveEnabled(
  sb: SupabaseClient,
  tenantId: string,
  enabled: boolean,
): Promise<void> {
  const { error } = await sb.rpc('archive_enabled_set', { p_tenant: tenantId, p_enabled: enabled });
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------------------------- processors' zips

/**
 * The tenant's processors' folder (tenant_archive.processor_folder, R165, decision 925), or null when unset. Read on
 * its own so the archive panel still works on a database before 20261102190000 (the column missing reads as unset).
 */
export async function loadProcessorFolder(sb: SupabaseClient, tenantId: string): Promise<string | null> {
  const { data, error } = await sb
    .from('tenant_archive')
    .select('processor_folder')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) return null;
  return ((data as { processor_folder?: string | null } | null)?.processor_folder ?? null) || null;
}

/** A tenant admin sets the processors' folder ('' for the default, <archive folder>/Field Data). */
export async function setProcessorFolder(
  sb: SupabaseClient,
  tenantId: string,
  folder: string,
): Promise<void> {
  const { error } = await sb.rpc('archive_processor_folder_set', { p_tenant: tenantId, p_folder: folder });
  if (error) throw new Error(error.message);
}
